import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUser, FaPhone, FaCalculator } from 'react-icons/fa';
import LocationSuggestions from './LocationSuggestions';
import DistanceCalculator from './DistanceCalculator';
import SimpleVehicleSelector from './SimpleVehicleSelector';
import { getMinimumTime, isToday, convertTo12HourFormat, convertTo24HourFormat, calculateFare } from '../utils/helpers';
import { sendTelegramNotification } from '../services/telegramNotification';
import { debugGoogleMapsAPI } from '../utils/debugHelper';
import './SimpleBookingForm.css';

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
  const options = [];
  
  for (let hour = 1; hour <= 12; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const minuteStr = minute.toString().padStart(2, '0');
      const hour24AM = hour === 12 ? 0 : hour;
      const hour24PM = hour === 12 ? 12 : hour + 12;
      
      // AM option
      options.push({
        display: `${hour}:${minuteStr} AM`,
        value: `${hour24AM.toString().padStart(2, '0')}:${minuteStr}`
      });
      
      // PM option
      options.push({
        display: `${hour}:${minuteStr} PM`,
        value: `${hour24PM.toString().padStart(2, '0')}:${minuteStr}`
      });
    }
  }
  
  return options.sort((a, b) => a.value.localeCompare(b.value));
};

const SimpleBookingForm = ({ onEstimate, initialData = null }) => {
  const [formData, setFormData] = useState(
    initialData || {
      pickupLocation: '',
      dropoffLocation: '',
      pickupDate: '',
      pickupTime: '',
      returnDate: '',
      customerName: '',
      customerPhone: '',
      vehicleType: 'sedan',
      tripType: 'oneway'
    }
  );

  const [distance, setDistance] = useState(initialData?.distance || 0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [pickupCoords, setPickupCoords] = useState(initialData?.pickupCoords || null);
  const [dropoffCoords, setDropoffCoords] = useState(initialData?.dropoffCoords || null);
  const [hasInitialDistance, setHasInitialDistance] = useState(!!initialData?.distance);
  const [displayTime, setDisplayTime] = useState(
    initialData?.pickupTime ? convertTo12HourFormat(initialData.pickupTime) : ''
  );

  // Set default date and time only if no initial data
  useEffect(() => {
    if (!initialData) {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = getMinimumTime();
      
      const defaultDisplayTime = convertTo12HourFormat(timeStr);
      setFormData(prev => ({
        ...prev,
        pickupDate: dateStr,
        pickupTime: timeStr
      }));
      setDisplayTime(defaultDisplayTime);
    }
    
    // Debug Google Maps API on component mount (development only)
    if (process.env.NODE_ENV === 'development') {
      debugGoogleMapsAPI();
    }
  }, [initialData]);

  // Trigger distance calculation when returning with coordinates
  useEffect(() => {
    if (initialData && initialData.pickupCoords && initialData.dropoffCoords && initialData.distance) {
      // Distance is already available from initialData, no need to recalculate
      setDistance(initialData.distance);
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

  };

  const handleTimeChange = (e) => {
    const time12 = e.target.value;
    setDisplayTime(time12);
    
    // Convert to 24-hour format for backend storage
    if (time12) {
      const time24 = convertTo24HourFormat(time12);
      setFormData(prev => ({
        ...prev,
        pickupTime: time24
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        pickupTime: ''
      }));
    }
    
    // Clear error when user selects a time
    if (errors.pickupTime) {
      setErrors(prev => ({
        ...prev,
        pickupTime: ''
      }));
    }
  };

  const handleLocationSelect = (type, location, coords) => {
    setFormData(prev => ({
      ...prev,
      [type]: location
    }));
    
    if (type === 'pickupLocation') {
      setPickupCoords(coords);
    } else {
      setDropoffCoords(coords);
    }
    
    // Reset the initial distance flag when locations change
    setHasInitialDistance(false);
  };

  const handleDistanceUpdate = (newDistance) => {
    // Only update distance if we don't already have initial distance
    if (!hasInitialDistance) {
      setDistance(newDistance);
    }
  };

  const validateForm = () => {
    let validationErrors = {};

    if (!formData.pickupLocation.trim()) {
      validationErrors.pickupLocation = 'From location is required';
    }
    if (!formData.dropoffLocation.trim()) {
      validationErrors.dropoffLocation = 'To location is required';
    }
    if (!formData.pickupDate) {
      validationErrors.pickupDate = 'Date is required';
    }
    if (formData.tripType === 'roundtrip' && !formData.returnDate) {
      validationErrors.returnDate = 'Return date is required for round trips';
    }
    if (formData.tripType === 'roundtrip' && formData.returnDate && formData.pickupDate) {
      const pickupDate = new Date(formData.pickupDate);
      const returnDate = new Date(formData.returnDate);
      if (returnDate <= pickupDate) {
        validationErrors.returnDate = 'Return date must be after pickup date';
      }
    }
    if (!displayTime) {
      validationErrors.pickupTime = 'Please select a time';
    }
    if (!formData.customerName.trim()) {
      validationErrors.customerName = 'Name is required';
    }
    if (!formData.customerPhone.trim()) {
      validationErrors.customerPhone = 'Contact number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.customerPhone.replace(/\D/g, ''))) {
      validationErrors.customerPhone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleGetEstimate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (distance === 0) {
      alert('Please wait for distance calculation to complete or enter valid locations.');
      return;
    }

    setIsCalculating(true);
    
    try {
      // Calculate fare before sending notification
      const fareDetails = calculateFare(
        distance, 
        formData.vehicleType, 
        formData.tripType, 
        formData.pickupDate, 
        formData.returnDate
      );
      
      // Prepare booking data with distance, coordinates, and fare
      const bookingDataWithDistance = {
        ...formData,
        distance: distance,
        pickupCoords: pickupCoords,
        dropoffCoords: dropoffCoords,
        estimatedFare: fareDetails.totalFare
      };
      
      // Send Telegram notification to admin
      try {
        await sendTelegramNotification(bookingDataWithDistance);
        console.log('Admin notified via Telegram');
      } catch (telegramError) {
        console.warn('Telegram notification failed:', telegramError);
        // Continue with booking even if Telegram fails
      }
      
      // Navigate to estimation page
      onEstimate(bookingDataWithDistance);
    } catch (error) {
      console.error('Error preparing estimation:', error);
      alert('Error preparing estimation. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <motion.div 
      id="booking-section"
      className="simple-booking-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="booking-card">
        <div className="booking-header">
          <h2 className="booking-title">Book One Way Drop Taxi </h2>
          <p className="booking-subtitle">Affordable One Way Cabs in Tamilnadu</p>
        </div>

        <form onSubmit={handleGetEstimate} className="simple-booking-form">
          {/* Trip Type - Moved to Top */}
          <div className="form-section">
            <div className="form-group">
              <label className="form-label">Trip Type</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="tripType"
                    value="oneway"
                    checked={formData.tripType === 'oneway'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-custom"></span>
                  <span className="radio-label">One Way</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="tripType"
                    value="roundtrip"
                    checked={formData.tripType === 'roundtrip'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-custom"></span>
                  <span className="radio-label">Round Trip</span>
                </label>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="form-section">
            <div className="location-row">
              <div className="form-group">
                <label className="form-label">
                  <FaMapMarkerAlt className="label-icon" />
                  From Location *
                </label>
                <LocationSuggestions
                  value={formData.pickupLocation}
                  onChange={handleInputChange}
                  onLocationSelect={(location, coords) => handleLocationSelect('pickupLocation', location, coords)}
                  placeholder="Enter pickup location"
                  error={errors.pickupLocation}
                  name="pickupLocation"
                />
                {errors.pickupLocation && <span className="error-text">{errors.pickupLocation}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaMapMarkerAlt className="label-icon" />
                  To Location *
                </label>
                <LocationSuggestions
                  value={formData.dropoffLocation}
                  onChange={handleInputChange}
                  onLocationSelect={(location, coords) => handleLocationSelect('dropoffLocation', location, coords)}
                  placeholder="Enter drop-off location"
                  error={errors.dropoffLocation}
                  name="dropoffLocation"
                />
                {errors.dropoffLocation && <span className="error-text">{errors.dropoffLocation}</span>}
              </div>
            </div>

            {/* Date and Time in 2x2 Layout */}
            <div className="datetime-grid">
              <div className="form-group">
                <label className="form-label">
                  <FaCalendarAlt className="label-icon" />
                  Date *
                </label>
                <input
                  type="date"
                  name="pickupDate"
                  value={formData.pickupDate}
                  onChange={handleInputChange}
                  className={`form-input ${errors.pickupDate ? 'error' : ''}`}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.pickupDate && <span className="error-text">{errors.pickupDate}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaClock className="label-icon" />
                  Time *
                </label>
                <select
                  name="pickupTime"
                  value={displayTime}
                  onChange={handleTimeChange}
                  className={`form-input ${errors.pickupTime ? 'error' : ''}`}
                >
                  <option value="">Select time</option>
                  {generateTimeOptions().map((time) => (
                    <option key={time.value} value={time.display}>
                      {time.display}
                    </option>
                  ))}
                </select>
                {errors.pickupTime && <span className="error-text">{errors.pickupTime}</span>}
              </div>

              {/* Return Date - Only show for round trips */}
              {formData.tripType === 'roundtrip' && (
                <>
                  <div className="form-group">
                    <label className="form-label">
                      <FaCalendarAlt className="label-icon" />
                      Return Date *
                    </label>
                    <input
                      type="date"
                      name="returnDate"
                      value={formData.returnDate}
                      onChange={handleInputChange}
                      className={`form-input ${errors.returnDate ? 'error' : ''}`}
                      min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                    />
                    {errors.returnDate && <span className="error-text">{errors.returnDate}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FaClock className="label-icon" />
                      Return Time
                    </label>
                    <select
                      name="returnTime"
                      className="form-input"
                    >
                      <option value="">Select return time</option>
                      {generateTimeOptions().map((time) => (
                        <option key={time.value} value={time.display}>
                          {time.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FaUser className="label-icon" />
                  Name *
                </label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`form-input ${errors.customerName ? 'error' : ''}`}
                />
                {errors.customerName && <span className="error-text">{errors.customerName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaPhone className="label-icon" />
                  Contact Number *
                </label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit phone number"
                  className={`form-input ${errors.customerPhone ? 'error' : ''}`}
                />
                {errors.customerPhone && <span className="error-text">{errors.customerPhone}</span>}
              </div>
            </div>

            {/* Vehicle Selection */}
            <div className="form-group">
              <label className="form-label">Select Vehicle</label>
              <SimpleVehicleSelector
                selectedVehicle={formData.vehicleType}
                onVehicleSelect={(vehicleType) => handleInputChange({ target: { name: 'vehicleType', value: vehicleType } })}
              />
            </div>

            {/* Distance Calculator (Hidden) */}
            <div style={{ display: 'none' }}>
              <DistanceCalculator
                pickupCoords={pickupCoords}
                dropoffCoords={dropoffCoords}
                pickupLocation={formData.pickupLocation}
                dropoffLocation={formData.dropoffLocation}
                onDistanceUpdate={handleDistanceUpdate}
                onCalculating={setIsCalculating}
              />
            </div>
          </div>

          {/* Get Estimate Button */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-estimate"
              disabled={isCalculating}
            >
              {isCalculating ? (
                <>
                  <div className="loading-spinner"></div>
                  Calculating...
                </>
              ) : (
                <>
                  <FaCalculator className="btn-icon" />
                  Get Estimate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default SimpleBookingForm;
